import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LumivetaLogo from '../../components/LumivetaLogo';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import api from '../../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password - Lumiveta</title>
        <meta name="description" content="Reset your Lumiveta account password" />
      </Helmet>

      <div className="min-h-dvh bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <LumivetaLogo size="large" />
            </Link>
          </div>

          <div className="glass rounded-2xl p-8 border border-primary/20">
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} color="#10B981" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                  Check Your Email
                </h2>
                <p className="text-muted-foreground mb-6">
                  If an account exists with that email, we've sent password reset instructions.
                </p>
                <Link to="/login">
                  <Button variant="outline" fullWidth>
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Icon name="Key" size={32} color="#FFD700" />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                    Forgot Password?
                  </h2>
                  <p className="text-muted-foreground">
                    Enter your email and we'll send you reset instructions
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20">
                    <p className="text-sm text-error">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="bg-input border-border"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    disabled={loading}
                    className="gold-glow"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Icon name="ArrowLeft" size={16} />
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            © 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™
          </p>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
